import { Component, ChangeDetectionStrategy, inject, input, output, OnInit, effect, signal, computed } from '@angular/core';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { startWith } from 'rxjs';
import Swal from 'sweetalert2';
import { FormMode } from 'app/core/constants/form-mode';
import { AppSettings } from '@services/settings/app-settings.service';
import { CatalogService } from '@services/data/catalog.service';
import { SheetSelectComponent } from 'app/core/components/sheet-select/sheet-select.component';
import { MobileFooterComponent } from 'app/core/components/mobile-footer/mobile-footer.component';
import { PartyFormComponent } from '../../../management-parties/components/party-form/party-form.component';
import { AddBenefitService } from './add-benefit.service';

const BENEFIT_TYPES = [
  { id: 'alarm', name: 'Alarma' },
  { id: 'sensor', name: 'Sensor' },
  { id: 'gps', name: 'GPS' },
  { id: 'security_film', name: 'Lámina de seguridad' },
  { id: 'roof_rack', name: 'Portaequipajes' },
  { id: 'hands_free', name: 'Manos libres' },
  { id: 'glass_engraving', name: 'Grabado de vidrios' },
  { id: 'license', name: 'Licencia de conducir' },
  { id: 'notary', name: 'Gastos notariales' },
  { id: 'rental_car', name: 'Arriendo / Uber' },
  { id: 'tow_truck', name: 'Grúa' },
  { id: 'custody', name: 'Custodia' }
];

const TYPE_RULE_SLUG: Record<string, string> = {
  alarm: 'BENEFIT_ACCESSORIES',
  sensor: 'BENEFIT_ACCESSORIES',
  gps: 'BENEFIT_ACCESSORIES',
  security_film: 'BENEFIT_ACCESSORIES',
  roof_rack: 'BENEFIT_ACCESSORIES',
  hands_free: 'BENEFIT_ACCESSORIES',
  glass_engraving: 'BENEFIT_GLASS_ENGRAVING',
  rental_car: 'BENEFIT_UBER',
  custody: 'BENEFIT_CUSTODY'
};

@Component({
  selector: 'app-add-benefit',
  templateUrl: './add-benefit.component.html',
  styleUrls: ['./add-benefit.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    CurrencyPipe,
    PartyFormComponent,
    SheetSelectComponent,
    MobileFooterComponent
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [AddBenefitService]
})
export class AddBenefitComponent implements OnInit {
  partyInput = input.required<any>();
  vehiclesInput = input.required<any[]>();
  onClose = output<boolean>();
  benefitAdded = output<void>();

  private fb = inject(FormBuilder);
  private addBenefitService = inject(AddBenefitService);
  private catalogService = inject(CatalogService);
  readonly appSettings = inject(AppSettings);

  public partyForm!: FormGroup;
  public benefitForm!: FormGroup;
  public formMode = FormMode;
  public benefitTypes = BENEFIT_TYPES;
  public isSaving = signal(false);

  private formKm = signal<number | null>(null);
  private formAmount = signal<number | null>(null);
  selectedType = signal<string | null>(null);

  /** Tope estimado desde catálogo de rules (no hardcode). */
  capPreview = computed(() => {
    this.catalogService.rules();
    const type = this.selectedType();
    if (!type) return null;

    const fmr = Number(this.catalogService.getRuleBySlug('FMR_VALUE')?.value || 0);
    if (!(fmr > 0)) return null;

    let rule: any = null;
    if (type === 'tow_truck') {
      const km = Number(this.formKm() || 0);
      const grua = (this.catalogService.rules() || [])
        .filter((r: any) => String(r.slug || '').toUpperCase().startsWith('GRUA_') && r.isActive !== false)
        .sort((a: any, b: any) => Number(a.minRange || 0) - Number(b.minRange || 0));
      if (km > 0) {
        rule = grua.find((r: any) =>
          km >= Number(r.minRange || 0) && km <= Number(r.maxRange || Number.POSITIVE_INFINITY)
        ) || null;
      }
      if (!rule) {
        rule = [...grua].sort((a, b) => Number(b.value) - Number(a.value))[0] || null;
      }
    } else {
      const slug = TYPE_RULE_SLUG[type];
      rule = slug ? this.catalogService.getRuleBySlug(slug) : null;
      if (rule && rule.isActive === false) rule = null;
    }

    if (!rule) return { hasCap: false as const };

    const percent = Number(rule.value || 0);
    const capAmount = Math.round(fmr * (percent / 100));
    const amount = Number(this.formAmount() || 0);
    return {
      hasCap: true as const,
      capAmount,
      capPercent: percent,
      capRuleSlug: rule.slug,
      overCap: amount > 0 && amount > capAmount
    };
  });

  private Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 1400,
    timerProgressBar: true
  });

  constructor() {
    effect(() => {
      const party = this.partyInput();
      if (this.partyForm && party) {
        this.partyForm.patchValue(party);
      }
    });
  }

  get isTowTruck(): boolean {
    return this.benefitForm?.get('type')?.value === 'tow_truck';
  }

  vehicleSelectItems(): any[] {
    return (this.vehiclesInput() || []).map((v: any) => ({
      ...v,
      selectLabel: `${v.plate} - ${v.brandName || v.brand?.name || ''} ${v.modelName || v.model?.name || ''} - ${v.year || ''}`
        .replace(/\s+/g, ' ')
        .trim()
    }));
  }

  ngOnInit(): void {
    this.partyForm = this.fb.group({
      uuid: [null],
      rut: [null],
      partyTypeSlug: [null],
      names: [null],
      birthday: [null],
      mail: [null],
      mobileNumber: [null],
      groupSlug: [null],
      entityCode: [null]
    });

    this.benefitForm = this.fb.group({
      vehicleUuid: [null, Validators.required],
      type: [null, Validators.required],
      km: [null],
      date: [new Date().toISOString().split('T')[0], Validators.required],
      amount: [null, [Validators.required, Validators.min(1)]]
    });

    this.benefitForm.get('type')?.valueChanges.pipe(startWith(this.benefitForm.get('type')?.value)).subscribe((type) => {
      this.selectedType.set(type || null);
      const kmCtrl = this.benefitForm.get('km');
      if (type === 'tow_truck') {
        kmCtrl?.setValidators([Validators.required, Validators.min(1)]);
      } else {
        kmCtrl?.clearValidators();
        kmCtrl?.setValue(null, { emitEvent: false });
      }
      kmCtrl?.updateValueAndValidity({ emitEvent: false });
    });

    this.benefitForm.get('km')?.valueChanges.pipe(startWith(this.benefitForm.get('km')?.value)).subscribe((km) => {
      this.formKm.set(km != null && km !== '' ? Number(km) : null);
    });

    this.benefitForm.get('amount')?.valueChanges.pipe(startWith(this.benefitForm.get('amount')?.value)).subscribe((amount) => {
      this.formAmount.set(amount != null && amount !== '' ? Number(amount) : null);
    });

    if (this.partyInput()) {
      this.partyForm.patchValue(this.partyInput());
    }
    const vehicles = this.vehiclesInput() || [];
    if (vehicles.length === 1) {
      this.benefitForm.get('vehicleUuid')?.setValue(vehicles[0].uuid);
    }
  }

  submit(): void {
    if (this.benefitForm.invalid) {
      this.benefitForm.markAllAsTouched();
      this.Toast.fire({
        icon: 'warning',
        title: this.isTowTruck
          ? 'Complete tipo, vehículo, km, fecha y monto'
          : 'Complete el tipo, vehículo, fecha y monto'
      });
      return;
    }
    const party = this.partyInput();
    if (!party?.uuid) {
      this.Toast.fire({ icon: 'error', title: 'No se encontró el socio' });
      return;
    }

    const preview = this.capPreview();
    const send = () => {
      this.isSaving.set(true);
      const type = this.benefitForm.get('type')?.value;
      const payload: any = {
        partyUuid: party.uuid,
        vehicleUuid: this.benefitForm.get('vehicleUuid')?.value,
        type,
        date: this.benefitForm.get('date')?.value,
        amount: this.benefitForm.get('amount')?.value
      };
      if (type === 'tow_truck') {
        payload.km = Number(this.benefitForm.get('km')?.value);
      }
      this.addBenefitService.createBenefit(payload).subscribe({
        next: () => {
          this.isSaving.set(false);
          Swal.fire({
            icon: 'success',
            title: 'Solicitud generada',
            text: 'El beneficio fue enviado al flujo de aprobación.',
            confirmButtonText: 'Entendido'
          });
          this.benefitAdded.emit();
        },
        error: (err) => {
          this.isSaving.set(false);
          Swal.fire('Error', err.error?.error || err.error?.message || 'No se pudo generar la solicitud', 'error');
        }
      });
    };

    if (preview?.hasCap && preview.overCap) {
      Swal.fire({
        icon: 'warning',
        title: 'Monto sobre el tope',
        html: `El monto supera el tope de <strong>${preview.capPercent}% FMR</strong>
          (${preview.capAmount.toLocaleString('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 })}).
          <br>¿Desea enviar de todos modos?`,
        showCancelButton: true,
        confirmButtonText: 'Sí, enviar',
        cancelButtonText: 'Revisar'
      }).then((res) => {
        if (res.isConfirmed) send();
      });
      return;
    }

    send();
  }
}
